import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import {
  FiFileText,
  FiSearch,
  FiUser,
  FiFilter,
  FiChevronRight,
  FiLoader,
  FiAlertCircle
} from 'react-icons/fi';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { customerService } from '../services/customerService';
import { statementService } from '../services/statementService';
import StatementPreview from '../components/statements/StatementPreview';
import { useClient } from '../context/ClientContext';

export default function CustomerStatements() {
  const { activeClient } = useClient();
  const [customers, setCustomers] = useState([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [statementData, setStatementData] = useState(null);
  const [isLoadingStatement, setIsLoadingStatement] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!activeClient?.id) return;

      setIsLoadingCustomers(true);
      try {
        const data = await customerService.getCustomers(activeClient.id);
        setCustomers(data || []);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      } finally {
        setIsLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, [activeClient?.id]);

  useEffect(() => {
    const generateStatement = async () => {
      if (!selectedCustomerId || !dateRange.start || !dateRange.end) return;

      setIsLoadingStatement(true);
      setError(null);

      try {
        const data = await statementService.getCustomerStatement(
          selectedCustomerId,
          dateRange.start,
          dateRange.end
        );
        setStatementData(data);
      } catch (err) {
        setError('Failed to generate statement. Please try again.');
        setStatementData(null);
      } finally {
        setIsLoadingStatement(false);
      }
    };

    generateStatement();
  }, [selectedCustomerId, dateRange]);

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (customer.email && customer.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="mx-auto max-w-[1500px] animate-in fade-in pb-7 duration-300">
      <div className="mb-5 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div>
          <h1 className="flex items-center gap-2 text-[22px] font-medium tracking-tight text-slate-900">
            <SafeIcon icon={FiFileText} className="text-[21px] text-emerald-500" />
            Customer Statements
          </h1>
          <p className="mt-1 text-[13px] font-normal text-slate-500">
            Generate professional balance summaries for your clients.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-5 xl:col-span-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
              Statement settings
            </h3>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-[11px] font-medium text-slate-600">
                  Search customer
                </label>
                <div className="relative">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-slate-400"
                  />
                  <input
                    type="text"
                    placeholder="Type a name..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-[13px] font-normal text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/10"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-medium text-slate-600">
                  Statement period
                </label>
                <div className="grid grid-cols-2 gap-2.5">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(event) => setDateRange({ ...dateRange, start: event.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[12px] font-medium text-slate-700 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/10"
                  />
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(event) => setDateRange({ ...dateRange, end: event.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[12px] font-medium text-slate-700 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/10"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex h-[440px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-3.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
                Select customer
              </span>
              <SafeIcon icon={FiFilter} className="text-[15px] text-slate-400" />
            </div>

            <div className="custom-scrollbar flex-1 overflow-y-auto">
              {isLoadingCustomers ? (
                <div className="flex h-full flex-col items-center justify-center text-slate-400">
                  <SafeIcon icon={FiLoader} className="mb-2 animate-spin text-xl text-emerald-500" />
                  <span className="text-[11px] font-medium">Loading customers...</span>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center p-6 text-center text-slate-400">
                  <span className="text-[13px] font-medium">No customers found.</span>
                </div>
              ) : (
                filteredCustomers.map((customer) => {
                  const isSelected = selectedCustomerId === customer.id;

                  return (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => setSelectedCustomerId(customer.id)}
                      className={`group flex w-full items-center justify-between border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-0 ${
                        isSelected ? 'bg-emerald-50/60' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[12px] font-medium ${
                          isSelected
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {customer.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-medium text-slate-800">
                            {customer.name}
                          </p>
                          <p className="mt-0.5 truncate text-[11px] font-normal text-slate-400">
                            {customer.email || 'Customer'}
                          </p>
                        </div>
                      </div>
                      <SafeIcon
                        icon={FiChevronRight}
                        className={`shrink-0 text-[15px] transition ${
                          isSelected
                            ? 'text-emerald-600'
                            : 'text-slate-300 opacity-0 group-hover:opacity-100'
                        }`}
                      />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 xl:col-span-8">
          {!selectedCustomerId ? (
            <div className="flex min-h-[500px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-slate-100 bg-white shadow-sm">
                <SafeIcon icon={FiUser} className="text-2xl text-slate-300" />
              </div>
              <h3 className="text-[18px] font-medium text-slate-800">Select a customer</h3>
              <p className="mt-2 max-w-sm text-[13px] font-normal leading-relaxed text-slate-500">
                Choose a customer from the list to generate their financial statement.
              </p>
            </div>
          ) : isLoadingStatement ? (
            <div className="flex min-h-[500px] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <SafeIcon icon={FiLoader} className="mb-3 animate-spin text-3xl text-emerald-500" />
              <p className="text-[12px] font-medium text-slate-500">Generating statement...</p>
            </div>
          ) : error ? (
            <div className="flex min-h-[500px] flex-col items-center justify-center rounded-2xl border border-dashed border-rose-200 bg-rose-50/50 p-8 text-center">
              <SafeIcon icon={FiAlertCircle} className="mb-3 text-3xl text-rose-500" />
              <p className="text-[13px] font-medium text-rose-700">{error}</p>
            </div>
          ) : statementData ? (
            <StatementPreview
              customer={statementData.customer}
              activity={statementData.activity}
              aging={statementData.aging}
              dateRange={dateRange}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}